import React, { useState, useEffect } from 'react';
import { Play, Pause, RefreshCw, TrendingUp, Users, CheckCircle, XCircle, Clock, DollarSign } from 'lucide-react';

const BatchValidation = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [stats, setStats] = useState(null);
  const [progress, setProgress] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState('Calculating...');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/validation/stats`);
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const startBatchValidation = async () => {
    if (!window.confirm('This will run validation on all 159 AA-rated people. This may take 2-4 hours and cost $50-150. Continue?')) {
      return;
    }

    setIsRunning(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/validation/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domains: ['marriage', 'divorce'],
          max_people: 159
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert('✅ Batch validation started! Check progress below.');
      } else {
        alert('❌ Failed to start validation');
        setIsRunning(false);
      }
    } catch (error) {
      console.error('Error starting validation:', error);
      alert('❌ Error starting validation');
      setIsRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-white text-4xl font-bold mb-2">🔱 Batch Validation</h1>
          <p className="text-cyan-400 text-lg">Grand Unified Marriage-Divorce-Remarriage Algorithm v7.6</p>
        </div>

        {/* Control Panel */}
        <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20 mb-8">
          <h2 className="text-white text-xl font-semibold mb-4">⚙️ Control Panel</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-white/60 text-sm mb-1">Target</p>
              <p className="text-white text-2xl font-bold">159 People</p>
              <p className="text-white/40 text-xs">AA-rated from Astro-Databank</p>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-white/60 text-sm mb-1">Domains</p>
              <p className="text-white text-2xl font-bold">Marriage + Divorce</p>
              <p className="text-white/40 text-xs">With remarriage analysis</p>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-white/60 text-sm mb-1">Estimated</p>
              <p className="text-white text-2xl font-bold">2-4 hours</p>
              <p className="text-white/40 text-xs">Cost: $50-150</p>
            </div>
          </div>

          <button
            onClick={startBatchValidation}
            disabled={isRunning}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold text-lg rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRunning ? (
              <>
                <RefreshCw className="w-6 h-6 animate-spin" />
                Running Batch Validation...
              </>
            ) : (
              <>
                <Play className="w-6 h-6" />
                Start Batch Validation (159 People)
              </>
            )}
          </button>
        </div>

        {/* Stats Dashboard */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-8 h-8 text-cyan-400" />
                <p className="text-white/60 text-sm">Processed</p>
              </div>
              <p className="text-white text-3xl font-bold">{stats.total_predictions || 0}</p>
              <p className="text-white/40 text-xs mt-1">Total predictions made</p>
            </div>

            <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-8 h-8 text-green-400" />
                <p className="text-white/60 text-sm">Correct</p>
              </div>
              <p className="text-green-400 text-3xl font-bold">{stats.correct_predictions || 0}</p>
              <p className="text-white/40 text-xs mt-1">Matched reality</p>
            </div>

            <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-2">
                <XCircle className="w-8 h-8 text-red-400" />
                <p className="text-white/60 text-sm">Failed</p>
              </div>
              <p className="text-red-400 text-3xl font-bold">{stats.failed_predictions || 0}</p>
              <p className="text-white/40 text-xs mt-1">Need correction</p>
            </div>

            <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-8 h-8 text-cyan-400" />
                <p className="text-white/60 text-sm">Accuracy</p>
              </div>
              <p className="text-cyan-400 text-3xl font-bold">{stats.accuracy_percentage?.toFixed(1) || 0}%</p>
              <p className="text-white/40 text-xs mt-1">Current accuracy</p>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {isRunning && (
          <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20 mb-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold">Validation Progress</h3>
              <p className="text-cyan-400">{progress}% Complete</p>
            </div>
            <div className="w-full bg-white/10 rounded-full h-4 overflow-hidden">
              <div
                className="bg-gradient-to-r from-cyan-500 to-blue-500 h-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-3 text-sm">
              <p className="text-white/60">
                <Clock className="w-4 h-4 inline mr-1" />
                Est. Time Remaining: {estimatedTime}
              </p>
              <p className="text-white/60">
                <DollarSign className="w-4 h-4 inline mr-1" />
                Est. Cost: $50-150
              </p>
            </div>
          </div>
        )}

        {/* Algorithm Info */}
        <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
          <h2 className="text-white text-xl font-semibold mb-4">📋 Algorithm Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white/5 rounded-lg p-4">
              <h3 className="text-cyan-400 font-semibold mb-2">Version</h3>
              <p className="text-white text-sm">Grand Unified v7.6 - Final Sealed</p>
            </div>

            <div className="bg-white/5 rounded-lg p-4">
              <h3 className="text-cyan-400 font-semibold mb-2">Core Axioms</h3>
              <ul className="text-white text-xs space-y-1">
                <li>• Marriage is a STATE, not an event</li>
                <li>• No marriage without MarriagePromise</li>
                <li>• No overlapping marriages</li>
                <li>• KP rules are authoritative</li>
              </ul>
            </div>

            <div className="bg-white/5 rounded-lg p-4">
              <h3 className="text-cyan-400 font-semibold mb-2">Analysis Steps</h3>
              <ul className="text-white text-xs space-y-1">
                <li>• Step 0: Data Extraction</li>
                <li>• Step 1-2: Marriage Promise/Denial</li>
                <li>• Step 3-6: Multiple Marriage Analysis</li>
                <li>• Step 7-11: Divorce Detection</li>
                <li>• Step 12-17: Timing & Output</li>
              </ul>
            </div>

            <div className="bg-white/5 rounded-lg p-4">
              <h3 className="text-cyan-400 font-semibold mb-2">Outputs</h3>
              <ul className="text-white text-xs space-y-1">
                <li>• Marriage Promise/Denial</li>
                <li>• Marriage Count & Timing</li>
                <li>• Divorce Potential</li>
                <li>• Remarriage Possibility</li>
                <li>• Extramarital Relations</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-6">
          <h3 className="text-white font-bold mb-3">📖 How It Works</h3>
          <ol className="text-white/80 text-sm space-y-2">
            <li>1. Click "Start Batch Validation" to begin processing all 159 people</li>
            <li>2. System generates astrological kernels for each person</li>
            <li>3. Runs Grand Unified Algorithm v7.6 on each kernel</li>
            <li>4. Searches Wikipedia/Google for actual life events</li>
            <li>5. Compares algorithm predictions vs real events</li>
            <li>6. Creates correction tickets for mismatches</li>
            <li>7. Review tickets in /validation dashboard</li>
            <li>8. Approve corrections and re-run until 100% accuracy</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default BatchValidation;
